/**
 * CR120: Preview Panel with Dock Modes
 * CR150: Added Draft/Published toggle, proofable debug output, cache-bust sync
 * CR220: Added real data preview source selector
 * CR225: Added fixture selector + payload indicators
 * CR305: Added debug render signature strip for pipeline verification
 * 
 * Enhanced preview panel with right/bottom docking and seasonal preview.
 * Includes takeover theme resolution and debug panel.
 */

import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  Monitor,
  Smartphone,
  Eye,
  RefreshCw,
  PanelBottom,
  PanelRight,
  Maximize2,
  Minimize2,
  Sparkles,
  Bug,
  ChevronDown,
} from "lucide-react";
import { DealFinderPreview } from "./DealFinderPreview";
import { PreviewSourceToggle } from "./PreviewSourceToggle";
import { PreviewStatusPill } from "./PreviewStatusPill";
import { PublishedLayoutInfo } from "./PublishedLayoutInfo";
import { PreviewDebugLine } from "./PreviewDebugLine";
import { PreviewDataSourceSelector } from "./PreviewDataSourceSelector";
import { PreviewFixtureSelector } from "./PreviewFixtureSelector";
import { PayloadIndicators } from "./PayloadIndicators";
import { PreviewDebugPanel } from "@/components/admin/preview/PreviewDebugPanel";
import { DealCardRenderSignatureStrip } from "@/components/debug/DealCardRenderSignatureStrip";
import { buildDealCardRenderSignature, type DealSource } from "@/lib/deals/debugDealCardSignature";
import { useLayoutPreviewSource, type PreviewSource } from "@/hooks/useLayoutPreviewSource";
import { useActiveLayout, type LayoutEnvironment } from "@/hooks/useActiveLayout";
import { useLayoutPreviewDeals } from "@/hooks/useLayoutPreviewDeals";
import { computeLayoutUxHash } from "@/lib/layout-studio/uxHashUtils";
import type { LayoutJson } from "@/lib/layout-studio/types";
import type { 
  PreviewDockMode, 
  SeasonalPreviewId, 
  BackgroundConfig,
  TakeoverConfig,
} from "@/lib/layout-studio/pageStylingTypes";
import { 
  MOCK_SEASONAL_THEMES, 
  MOCK_TAKEOVER_THEMES,
  type MockTakeoverTheme,
} from "@/lib/layout-studio/pageStylingTypes";
import { FoldLineControl } from "@/components/deals/FoldLineControl";
import { useFoldLineSettings, mapCardSizeToFoldKey } from "@/hooks/useFoldLineSettings";
import type { FoldLineHeight } from "@/components/deals/FoldLineOverlay";
import { 
  type PreviewFixtureId, 
  getFixtureDeals,
} from "@/lib/deals/previewFixtures";
import { toUiDeals } from "@/lib/deals/toUiDeal";

interface PreviewPanelProps {
  layout: LayoutJson;
  previewKey: string | number;
  dockMode: PreviewDockMode;
  background?: BackgroundConfig;
  takeover?: TakeoverConfig;
  onDockModeChange: (mode: PreviewDockMode) => void;
  /** CR150: Callback when publish/activate succeeds for auto-switch */
  onPublishSuccess?: () => void;
  /** CR150: Currently selected preset name for draft mode */
  selectedPresetName?: string;
}

export function PreviewPanel({
  layout,
  previewKey,
  dockMode,
  background,
  takeover,
  onDockModeChange,
  selectedPresetName,
}: PreviewPanelProps) {
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [seasonalPreview, setSeasonalPreview] = useState<SeasonalPreviewId>("default");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [debugOpen, setDebugOpen] = useState(false);
  
  // CR225: Fixture selector state
  const [fixtureId, setFixtureId] = useState<PreviewFixtureId>("real_data");
  
  // CR154: Use new fold line settings
  const { settings: foldSettings, setEnabled } = useFoldLineSettings();
  const foldHeight: FoldLineHeight = foldSettings.enabled ? "normal" : "off";
  const setFoldHeight = (h: FoldLineHeight) => setEnabled(h !== "off");

  // CR220: Real data preview source
  const {
    deals: realDeals,
    meta: previewMeta,
    source: dataSource,
    batchRunId,
    batchRuns,
    batchRunsLoading,
    loading: dealsLoading,
    setSource: setDataSource,
    setBatchRunId,
    refresh: refreshDeals,
    fetchBatchRuns,
  } = useLayoutPreviewDeals(20);
  
  // CR225: Apply fixture if selected, otherwise use real data
  const fixtureDeals = useMemo(() => {
    if (fixtureId === "real_data") return null;
    const rawFixtures = getFixtureDeals(fixtureId);
    return toUiDeals(rawFixtures, "mock");
  }, [fixtureId]);
  
  // Final deals to show (fixture takes priority)
  const previewDeals = fixtureDeals && fixtureDeals.length > 0 ? fixtureDeals : realDeals;
  const isUsingFixture = fixtureId !== "real_data" && fixtureDeals && fixtureDeals.length > 0;

  // CR150: Preview source state
  const { 
    source, 
    setSource, 
    isPublishedMode, 
    publishedEnvironment 
  } = useLayoutPreviewSource();

  // CR150: Fetch active layout for published modes
  const stagingLayout = useActiveLayout("staging");
  const liveLayout = useActiveLayout("publish");

  // CR150: Select the appropriate active layout based on source
  const activeLayoutResult = source === "published_staging" 
    ? stagingLayout 
    : source === "published_live" 
      ? liveLayout 
      : null;

  // CR150: Determine which layout to render
  const effectiveLayout = isPublishedMode && activeLayoutResult?.preset
    ? activeLayoutResult.layout
    : layout;

  // CR170 DEBUG: Log what layout PreviewPanel is passing
  console.log('[CR170-PreviewPanel] isPublishedMode:', isPublishedMode, 
    'effectiveLayout.dealCardUX.detailsSections:', 
    effectiveLayout?.dealCardUX?.detailsSections?.map(s => `${s.key}:${s.enabled}`).join(', '));

  // CR150: Compute uxHash from the effective layout being rendered
  const uxHash = useMemo(() => computeLayoutUxHash(effectiveLayout), [effectiveLayout]);

  // CR150: Get preset name for display
  const effectivePresetName = isPublishedMode 
    ? (activeLayoutResult?.preset?.name || null)
    : (selectedPresetName || null);

  // CR150: Handle source change with cache-bust
  const handleSourceChange = useCallback((newSource: PreviewSource) => {
    setSource(newSource);
    // Force refresh when switching to published modes
    if (newSource === "published_staging") {
      stagingLayout.syncLayout();
    } else if (newSource === "published_live") {
      liveLayout.syncLayout();
    }
    setRefreshKey(k => k + 1);
  }, [setSource, stagingLayout, liveLayout]);

  // CR150: Handle sync with toast feedback
  const handleSync = useCallback(async () => {
    if (!activeLayoutResult) return;
    
    const success = await activeLayoutResult.syncLayout();
    if (success) {
      toast.success("Layout synced", {
        description: `Fetched latest ${publishedEnvironment === "staging" ? "staging" : "live"} layout`,
      });
      setRefreshKey(k => k + 1);
    } else {
      toast.error("Sync failed", {
        description: "Couldn't fetch latest layout. Check console for details.",
      });
    }
  }, [activeLayoutResult, publishedEnvironment]);

  // Get seasonal theme colors for preview overlay
  const seasonalTheme = useMemo(() => {
    return MOCK_SEASONAL_THEMES.find((t) => t.id === seasonalPreview);
  }, [seasonalPreview]);

  // CR120: Resolve takeover theme from mocked themes by ID
  const resolvedTakeoverTheme = useMemo((): MockTakeoverTheme | null => {
    if (!takeover?.themeId || takeover.themeId === "none") return null;
    return MOCK_TAKEOVER_THEMES.find((t) => t.id === takeover.themeId) || null;
  }, [takeover?.themeId]);

  // Check if any takeover toggle is enabled
  const isTakeoverActive = useMemo(() => {
    if (!resolvedTakeoverTheme) return false;
    return takeover?.applyToHero || takeover?.applyToBackground || 
           takeover?.applyToCardsAccent || takeover?.applyToFilters;
  }, [resolvedTakeoverTheme, takeover]);

  // Compute background style for preview (respects takeover if background is enabled)
  const previewBackgroundStyle = useMemo((): React.CSSProperties => {
    // If takeover is active and applies to background, use takeover colors
    if (isTakeoverActive && takeover?.applyToBackground && resolvedTakeoverTheme) {
      // Use takeover theme styling - for now, apply a distinct visual
      if (resolvedTakeoverTheme.id === "black_friday_2024") {
        return { 
          background: "linear-gradient(135deg, hsl(0 0% 8%), hsl(0 0% 15%))",
          color: "hsl(45 100% 50%)",
        };
      }
      if (resolvedTakeoverTheme.id === "holiday_winter") {
        return { 
          background: "linear-gradient(135deg, hsl(210 60% 20%), hsl(200 50% 30%))",
        };
      }
      if (resolvedTakeoverTheme.id === "spring_refresh") {
        return { 
          background: "linear-gradient(135deg, hsl(140 40% 95%), hsl(100 50% 90%))",
        };
      }
    }

    if (!background) return {};
    
    switch (background.type) {
      case "solid":
        return { backgroundColor: `hsl(${background.solidColor || "0 0% 98%"})` };
      case "gradient": {
        const g = background.gradient || { color1: "0 0% 98%", color2: "220 15% 95%", angle: 135 };
        return { background: `linear-gradient(${g.angle}deg, hsl(${g.color1}), hsl(${g.color2}))` };
      }
      case "image": {
        const img = background.image;
        if (!img?.url) return { backgroundColor: "hsl(0 0% 98%)" };
        const style: React.CSSProperties = {
          backgroundImage: `url(${img.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
        if (img.blur) style.filter = `blur(${img.blur}px)`;
        if (img.opacity !== undefined) style.opacity = img.opacity / 100;
        return style;
      }
      case "takeover":
        // Fallback if type is takeover but no theme resolved
        return { backgroundColor: "hsl(0 0% 95%)" };
      default:
        return {};
    }
  }, [background, isTakeoverActive, takeover?.applyToBackground, resolvedTakeoverTheme]);

  // Debug state snapshot
  const debugSnapshot = useMemo(() => ({
    backgroundType: background?.type || "none",
    backgroundSolidColor: background?.solidColor,
    takeoverThemeId: takeover?.themeId || "none",
    takeoverResolved: resolvedTakeoverTheme?.name || null,
    takeoverToggles: {
      hero: takeover?.applyToHero || false,
      background: takeover?.applyToBackground || false,
      cards: takeover?.applyToCardsAccent || false,
      filters: takeover?.applyToFilters || false,
    },
    isTakeoverActive,
    seasonalPreview,
    viewport,
    dockMode,
  }), [background, takeover, resolvedTakeoverTheme, isTakeoverActive, seasonalPreview, viewport, dockMode]);

  // Refresh handler
  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  // Container classes based on dock mode
  const containerClass = dockMode === "bottom" 
    ? "w-full h-[400px]" 
    : "h-full";

  // CR150: Helper text based on source
  const helperText = source === "draft"
    ? "Changes update instantly. Publish/Activate to apply to Staging/Live."
    : `Showing the active preset for ${publishedEnvironment === "staging" ? "Staging" : "Live"}. Edit draft to make changes.`;

  return (
    <Card className={`overflow-hidden flex flex-col ${containerClass}`}>
      <CardHeader className="pb-2 border-b flex-shrink-0 space-y-3">
        {/* Row 1: Title + Status Pill + Preview Source Toggle */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Live Preview</CardTitle>
            <PreviewStatusPill
              source={source}
              hasActivePreset={!isPublishedMode || !!activeLayoutResult?.preset}
              onSourceChange={handleSourceChange}
            />
          </div>
          
          <PreviewSourceToggle
            value={source}
            onChange={handleSourceChange}
            disabled={activeLayoutResult?.loading}
          />
        </div>

        {/* CR150: Helper text */}
        <p className="text-[10px] text-muted-foreground">
          {helperText}
        </p>
        
        {/* CR150: Published Layout Info Block (only in published modes) */}
        {isPublishedMode && (
          <PublishedLayoutInfo
            environment={publishedEnvironment as "staging" | "publish"}
            preset={activeLayoutResult?.preset ?? null}
            lastSynced={activeLayoutResult?.lastSynced ?? null}
            syncing={activeLayoutResult?.syncing ?? false}
            onSync={handleSync}
          />
        )}

        {/* CR220: Real Data Source Selector */}
        <PreviewDataSourceSelector
          source={dataSource}
          onSourceChange={setDataSource}
          batchRunId={batchRunId}
          onBatchRunChange={setBatchRunId}
          batchRuns={batchRuns}
          batchRunsLoading={batchRunsLoading}
          onRefreshBatchRuns={fetchBatchRuns}
          meta={previewMeta}
          loading={dealsLoading}
          onRefresh={refreshDeals}
          compact
        />
        
        {/* CR225: Fixture Selector + Payload Indicators */}
        <div className="flex items-center gap-3 flex-wrap">
          <PreviewFixtureSelector
            value={fixtureId}
            onChange={setFixtureId}
            compact
          />
          
          <Separator orientation="vertical" className="h-4" />
          
          {/* Payload indicators for first deal */}
          <PayloadIndicators 
            deal={previewDeals[0] ?? null} 
            compact 
          />
          
          {isUsingFixture && (
            <Badge variant="secondary" className="text-[10px] h-5">
              Using Fixture
            </Badge>
          )}
        </div>

        {/* Row 2: Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Seasonal Preview Selector */}
          <div className="flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-muted-foreground" />
            <Select value={seasonalPreview} onValueChange={(v) => setSeasonalPreview(v as SeasonalPreviewId)}>
              <SelectTrigger className="h-7 w-[120px] text-xs">
                <SelectValue placeholder="Season..." />
              </SelectTrigger>
              <SelectContent>
                {MOCK_SEASONAL_THEMES.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id} className="text-xs">
                    {theme.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator orientation="vertical" className="h-4" />

          {/* Viewport Toggle */}
          <ToggleGroup 
            type="single" 
            value={viewport} 
            onValueChange={(v) => v && setViewport(v as "desktop" | "mobile")}
          >
            <ToggleGroupItem value="desktop" size="sm" className="h-7 w-7 p-0">
              <Monitor className="h-3.5 w-3.5" />
            </ToggleGroupItem>
            <ToggleGroupItem value="mobile" size="sm" className="h-7 w-7 p-0">
              <Smartphone className="h-3.5 w-3.5" />
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Dock Mode Toggle */}
          <ToggleGroup 
            type="single" 
            value={dockMode} 
            onValueChange={(v) => v && onDockModeChange(v as PreviewDockMode)}
          >
            <ToggleGroupItem value="right" size="sm" className="h-7 w-7 p-0">
              <PanelRight className="h-3.5 w-3.5" />
            </ToggleGroupItem>
            <ToggleGroupItem value="bottom" size="sm" className="h-7 w-7 p-0">
              <PanelBottom className="h-3.5 w-3.5" />
            </ToggleGroupItem>
          </ToggleGroup>

          <Separator orientation="vertical" className="h-4" />

          {/* CR-FoldLine: Card cutoff control */}
          <FoldLineControl 
            value={foldHeight} 
            onChange={setFoldHeight}
            showLabel={false}
            compact
          />

          {/* Actions */}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleRefresh}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0" 
            onClick={() => setIsFullscreen(!isFullscreen)}
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>

        {/* Seasonal Theme Indicator */}
        {seasonalPreview !== "default" && seasonalTheme && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              Previewing: {seasonalTheme.label}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {seasonalTheme.description}
            </span>
          </div>
        )}

        {/* Takeover Indicator */}
        {isTakeoverActive && resolvedTakeoverTheme && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] border-primary">
              Takeover: {resolvedTakeoverTheme.name}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {[
                takeover?.applyToHero && "Hero",
                takeover?.applyToBackground && "BG",
                takeover?.applyToCardsAccent && "Cards",
                takeover?.applyToFilters && "Filters",
              ].filter(Boolean).join(" • ")}
            </span>
          </div>
        )}

        {/* CR150: Preview Debug Panel with proof line */}
        <Collapsible open={debugOpen} onOpenChange={setDebugOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-muted-foreground">
              <Bug className="h-3 w-3 mr-1" />
              Preview Debug
              <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${debugOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {/* CR150: Proof line */}
            <PreviewDebugLine
              source={source}
              presetName={effectivePresetName}
              uxHash={uxHash}
            />
            
            {/* Existing debug info */}
            <div className="p-2 bg-muted/50 rounded text-[10px] font-mono space-y-1 max-h-32 overflow-auto">
              <div><span className="text-muted-foreground">bg.type:</span> {debugSnapshot.backgroundType}</div>
              {debugSnapshot.backgroundSolidColor && (
                <div><span className="text-muted-foreground">bg.color:</span> {debugSnapshot.backgroundSolidColor}</div>
              )}
              <div><span className="text-muted-foreground">takeover.id:</span> {debugSnapshot.takeoverThemeId}</div>
              <div><span className="text-muted-foreground">takeover.resolved:</span> {debugSnapshot.takeoverResolved || "—"}</div>
              <div><span className="text-muted-foreground">takeover.active:</span> {debugSnapshot.isTakeoverActive ? "✓" : "✗"}</div>
              <div>
                <span className="text-muted-foreground">toggles:</span>{" "}
                {debugSnapshot.takeoverToggles.hero ? "H" : "—"}
                {debugSnapshot.takeoverToggles.background ? "B" : "—"}
                {debugSnapshot.takeoverToggles.cards ? "C" : "—"}
                {debugSnapshot.takeoverToggles.filters ? "F" : "—"}
              </div>
              <div><span className="text-muted-foreground">seasonal:</span> {debugSnapshot.seasonalPreview}</div>
              <div><span className="text-muted-foreground">viewport:</span> {debugSnapshot.viewport}</div>
              <div><span className="text-muted-foreground">dock:</span> {debugSnapshot.dockMode}</div>
            </div>

            {/* CR150: Debug watermark indicator */}
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-medium">
              {source === "draft" ? "DRAFT" : source === "published_staging" ? "PUBLISHED – STAGING" : "PUBLISHED – LIVE"}
            </div>
            
            {/* CR173: Unified Preview Store Debug */}
            <PreviewDebugPanel className="mt-2" />
          </CollapsibleContent>
        </Collapsible>
      </CardHeader>

      <CardContent className="p-0 flex-1 overflow-auto" style={previewBackgroundStyle}>
        {/* Preview Container */}
        {isFullscreen ? (
          <div
            key={`${previewKey}-${refreshKey}-${seasonalPreview}-${uxHash}`}
            className="fixed inset-0 z-50 bg-background flex flex-col"
          >
            {/* Fullscreen toolbar */}
            <div className="flex-shrink-0 border-b p-3 flex items-center justify-between bg-muted/30">
              <span className="text-sm font-medium">Fullscreen Preview</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsFullscreen(false)}
              >
                <Minimize2 className="h-4 w-4 mr-2" />
                Exit Fullscreen
              </Button>
            </div>
            
            {/* Scrollable content area */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className={`mx-auto bg-background ${viewport === "mobile" ? "max-w-[375px]" : "max-w-full"}`}>
                <DealFinderPreview 
                  layout={effectiveLayout} 
                  previewKey={`${previewKey}-${refreshKey}-${uxHash}`}
                  deals={previewDeals}
                  dealsLoading={dealsLoading}
                  dataSource={dataSource}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            key={`${previewKey}-${refreshKey}-${seasonalPreview}-${uxHash}`}
            className={`mx-auto bg-background transition-all ${viewport === "mobile" ? "max-w-[375px]" : "max-w-full"}`}
          >
            <DealFinderPreview 
              layout={effectiveLayout} 
              previewKey={`${previewKey}-${refreshKey}-${uxHash}`}
              deals={previewDeals}
              dealsLoading={dealsLoading}
              dataSource={dataSource}
            />
            
            {/* CR305: Debug Render Signature Strip */}
            <div className="p-4">
              <DealCardRenderSignatureStrip
                sig={buildDealCardRenderSignature({
                  screen: "layout_editor",
                  deal: {
                    id: (previewDeals[0] as any)?._previewId ?? null,
                    dedupe_key: (previewDeals[0] as any)?.dedupe_key ?? null,
                    merchant_key: previewDeals[0]?.merchant?.toLowerCase().replace(/\s+/g, "-") ?? null,
                    deal_type: previewDeals[0]?.deal_category ?? null,
                    source: (isUsingFixture ? "fixture" : dataSource === "live" ? "production_deals" : dataSource === "mock" ? "mock" : "cr075_production_deals") as DealSource,
                  },
                  layout: {
                    environment: isPublishedMode 
                      ? (publishedEnvironment === "staging" ? "staging" : "publish")
                      : "draft",
                    active_preset_id: activeLayoutResult?.preset?.id ?? null,
                    preset_updated_at: activeLayoutResult?.preset?.updated_at ?? null,
                  },
                  theme: {
                    tokens_source: "layout_preset",
                    theme_id: null,
                  },
                  config: {
                    resolver: "layout_studio_resolver",
                    uses_deal_card_display_helpers: true,
                  },
                })}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
